import React, { useState } from 'react';
import {
  FileText,
  Users,
  BookOpen,
  Calendar,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  UserPlus,
  UsersRound,
  Briefcase,
  Target,
  UserCircle,
  Search,
  Mail,
  Bell,
  Send,
  X
} from 'lucide-react';
import useLearningAssignmentToolStore from '@/stores/AdminStore/LearningAssignmentToolStore';
import { User, LearningItem } from '@/services/PagesServices/LearningAssignmentToolService';
import './CreateAssignment.css';

const CreateAssignment: React.FC = () => {
  const { users: storeUsers, learningItems: storeLearningItems } = useLearningAssignmentToolStore();

  const [currentStep, setCurrentStep] = useState(1);
  const [assignmentName, setAssignmentName] = useState('');
  const [description, setDescription] = useState('');
  const [userSelectionType, setUserSelectionType] = useState<'individual' | 'team' | 'department' | 'role' | 'group'>('individual');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>(storeUsers || []);
  const [learningItems, setLearningItems] = useState<LearningItem[]>(storeLearningItems || []);

  // Update local state when store data changes
  React.useEffect(() => {
    if (storeUsers) {
      setUsers(storeUsers);
    }
  }, [storeUsers]);

  React.useEffect(() => {
    if (storeLearningItems) {
      setLearningItems(storeLearningItems);
    }
  }, [storeLearningItems]);

  const [itemTypeFilter, setItemTypeFilter] = useState('All Types');
  const [itemCategoryFilter, setItemCategoryFilter] = useState('All Category');
  const [dueDate, setDueDate] = useState('');
  const [enableReminders, setEnableReminders] = useState(false);
  const [reminderTiming, setReminderTiming] = useState('3 days before');
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [notificationMethod, setNotificationMethod] = useState<'email' | 'inapp' | 'both'>('both');

  const steps = [
    { number: 1, title: 'Name & Description', icon: FileText },
    { number: 2, title: 'Select Users', icon: Users },
    { number: 3, title: 'Select Learning Items', icon: BookOpen },
    { number: 4, title: 'Settings & Notifications', icon: Calendar },
    { number: 5, title: 'Review & Confirm', icon: CheckCircle }
  ];

  const handleUserToggle = (userId: string) => {
    setUsers(users.map(user =>
      user.id === userId ? { ...user, selected: !user.selected } : user
    ));
  };

  const handleLearningItemToggle = (itemId: string) => {
    setLearningItems(learningItems.map(item =>
      item.id === itemId ? { ...item, selected: !item.selected } : item
    ));
  };

  const selectedUsers = users.filter(u => u.selected);
  const selectedItems = learningItems.filter(i => i.selected);

  const filteredLearningItems = learningItems.filter(item => {
    const matchesType = itemTypeFilter === 'All Types' || item.type === itemTypeFilter;
    const matchesCategory = itemCategoryFilter === 'All Category' || item.category === itemCategoryFilter;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesCategory && matchesSearch;
  });

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="s-admin-ca-form-section">
            <h2 className="s-admin-ca-section-title">Assignment Details</h2>
            <p className="s-admin-ca-section-subtitle">Provide a name and description for this assignment</p>

            <div className="s-admin-ca-form-card">
              <div className="s-admin-ca-form-group">
                <label className="s-admin-ca-label">
                  Assignment Name <span className="s-admin-ca-required">*</span>
                </label>
                <input
                  type="text"
                  className="s-admin-ca-input"
                  placeholder="e.g., Q4 2025 Training Program"
                  value={assignmentName}
                  onChange={(e) => setAssignmentName(e.target.value)}
                />
              </div>

              <div className="s-admin-ca-form-group">
                <label className="s-admin-ca-label">Description (Optional)</label>
                <textarea
                  className="s-admin-ca-textarea"
                  placeholder="Add context or instructions for this assignment..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="s-admin-ca-form-section">
            <h2 className="s-admin-ca-section-title">Select Users</h2>
            <p className="s-admin-ca-section-subtitle">Choose who should receive this assignment</p>

            <div className="s-admin-ca-selection-methods">
              <button
                className={`s-admin-ca-method-btn ${userSelectionType === 'individual' ? 's-admin-ca-method-active' : ''}`}
                onClick={() => setUserSelectionType('individual')}
              >
                <UserPlus size={20} />
                <span>Individual</span>
              </button>
              <button
                className={`s-admin-ca-method-btn ${userSelectionType === 'team' ? 's-admin-ca-method-active' : ''}`}
                onClick={() => setUserSelectionType('team')}
              >
                <UsersRound size={20} />
                <span>By Team</span>
              </button>
              <button
                className={`s-admin-ca-method-btn ${userSelectionType === 'department' ? 's-admin-ca-method-active' : ''}`}
                onClick={() => setUserSelectionType('department')}
              >
                <Briefcase size={20} />
                <span>By Department</span>
              </button>
              <button
                className={`s-admin-ca-method-btn ${userSelectionType === 'role' ? 's-admin-ca-method-active' : ''}`}
                onClick={() => setUserSelectionType('role')}
              >
                <Target size={20} />
                <span>By Role</span>
              </button>
              <button
                className={`s-admin-ca-method-btn ${userSelectionType === 'group' ? 's-admin-ca-method-active' : ''}`}
                onClick={() => setUserSelectionType('group')}
              >
                <UserCircle size={20} />
                <span>By Group</span>
              </button>
            </div>

            <div className="s-admin-ca-search-filters">
              <div className="s-admin-ca-search-box">
                <Search size={18} className="s-admin-ca-search-icon" />
                <input
                  type="text"
                  className="s-admin-ca-search-input"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select className="s-admin-ca-filter-select">
                <option>Select Department</option>
                <option>Engineering</option>
                <option>Marketing</option>
                <option>Sales</option>
                <option>HR</option>
              </select>
            </div>

            <div className="s-admin-ca-users-table-container">
              <table className="s-admin-ca-users-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>USER</th>
                    <th>DEPARTMENT</th>
                    <th>JOB TITLE</th>
                    <th>TEAM</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="s-admin-ca-checkbox"
                          checked={user.selected}
                          onChange={() => handleUserToggle(user.id)}
                        />
                      </td>
                      <td>
                        <div className="s-admin-ca-user-cell">
                          <div className="s-admin-ca-user-avatar">{user.avatar}</div>
                          <div>
                            <div className="s-admin-ca-user-name">{user.name}</div>
                            <div className="s-admin-ca-user-email">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{user.department}</td>
                      <td>{user.jobTitle}</td>
                      <td>{user.team}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="s-admin-ca-form-section">
            <h2 className="s-admin-ca-section-title">Select Learning Items</h2>
            <p className="s-admin-ca-section-subtitle">Choose one or more courses, paths, or certifications</p>

            <div className="s-admin-ca-items-filters">
              <div className="s-admin-ca-search-box">
                <Search size={18} className="s-admin-ca-search-icon" />
                <input
                  type="text"
                  className="s-admin-ca-search-input"
                  placeholder="Search learning items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                className="s-admin-ca-filter-select"
                value={itemTypeFilter}
                onChange={(e) => setItemTypeFilter(e.target.value)}
              >
                <option>All Types</option>
                <option>Course</option>
                <option>Path</option>
                <option>Certification</option>
              </select>
              <select
                className="s-admin-ca-filter-select"
                value={itemCategoryFilter}
                onChange={(e) => setItemCategoryFilter(e.target.value)}
              >
                <option>All Category</option>
                <option>Leadership</option>
                <option>Technical</option>
                <option>Marketing</option>
                <option>Sales</option>
              </select>
            </div>

            {selectedItems.length > 0 && (
              <div className="s-admin-ca-selected-banner">
                <BookOpen size={16} />
                <span>{selectedItems.length} items selected</span>
                <button className="s-admin-ca-clear-btn" onClick={() => setLearningItems(learningItems.map(i => ({ ...i, selected: false })))}>
                  Clear all
                </button>
              </div>
            )}

            <div className="s-admin-ca-items-grid">
              {filteredLearningItems.map((item) => (
                <div
                  key={item.id}
                  className={`s-admin-ca-item-card ${item.selected ? 's-admin-ca-item-selected' : ''}`}
                  onClick={() => handleLearningItemToggle(item.id)}
                >
                  <div className="s-admin-ca-item-icon">
                    <BookOpen size={20} />
                  </div>
                  <div className="s-admin-ca-item-content">
                    <h4 className="s-admin-ca-item-title">{item.title}</h4>
                    <div className="s-admin-ca-item-meta">
                      <span className="s-admin-ca-item-badge">{item.type}</span>
                      <span className="s-admin-ca-item-detail">{item.category}</span>
                      <span className="s-admin-ca-item-detail">• {item.duration}</span>
                      <span className="s-admin-ca-item-detail">• {item.modules} modules</span>
                    </div>
                  </div>
                  {item.selected && (
                    <div className="s-admin-ca-item-check">
                      <CheckCircle size={20} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="s-admin-ca-form-section">
            <h2 className="s-admin-ca-section-title">Settings & Notifications</h2>
            <p className="s-admin-ca-section-subtitle">Configure deadlines and notification preferences</p>

            <div className="s-admin-ca-settings-grid">
              <div className="s-admin-ca-settings-card">
                <h3 className="s-admin-ca-card-title">Deadline & Reminders</h3>

                <div className="s-admin-ca-form-group">
                  <label className="s-admin-ca-label">Due Date (Optional)</label>
                  <input
                    type="date"
                    className="s-admin-ca-input"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    placeholder="dd-mm-yyyy"
                  />
                </div>

                <div className="s-admin-ca-form-group">
                  <label className="s-admin-ca-checkbox-label">
                    <input
                      type="checkbox"
                      className="s-admin-ca-checkbox"
                      checked={enableReminders}
                      onChange={(e) => setEnableReminders(e.target.checked)}
                    />
                    <span>Enable Reminders</span>
                  </label>
                  <p className="s-admin-ca-helper-text">Send reminder before due date</p>
                </div>

                {enableReminders && (
                  <div className="s-admin-ca-form-group">
                    <label className="s-admin-ca-label">Reminder Timing</label>
                    <select
                      className="s-admin-ca-select"
                      value={reminderTiming}
                      onChange={(e) => setReminderTiming(e.target.value)}
                    >
                      <option>Time before due date</option>
                      <option>1 day before</option>
                      <option>3 days before</option>
                      <option>1 week before</option>
                      <option>2 weeks before</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="s-admin-ca-settings-card">
                <h3 className="s-admin-ca-card-title">Notification Settings</h3>

                <div className="s-admin-ca-form-group">
                  <label className="s-admin-ca-checkbox-label">
                    <input
                      type="checkbox"
                      className="s-admin-ca-checkbox"
                      checked={notifyUsers}
                      onChange={(e) => setNotifyUsers(e.target.checked)}
                    />
                    <span>Notify Users</span>
                  </label>
                  <p className="s-admin-ca-helper-text">Send notification about new assignment</p>
                </div>

                <div className="s-admin-ca-form-group">
                  <label className="s-admin-ca-label">Notification Method</label>

                  <div className="s-admin-ca-radio-group">
                    <label className="s-admin-ca-radio-label">
                      <input
                        type="radio"
                        name="notification"
                        className="s-admin-ca-radio"
                        checked={notificationMethod === 'email'}
                        onChange={() => setNotificationMethod('email')}
                      />
                      <Mail size={16} />
                      <span>Email Only</span>
                    </label>

                    <label className="s-admin-ca-radio-label">
                      <input
                        type="radio"
                        name="notification"
                        className="s-admin-ca-radio"
                        checked={notificationMethod === 'inapp'}
                        onChange={() => setNotificationMethod('inapp')}
                      />
                      <Bell size={16} />
                      <span>In-App Only</span>
                    </label>

                    <label className="s-admin-ca-radio-label">
                      <input
                        type="radio"
                        name="notification"
                        className="s-admin-ca-radio"
                        checked={notificationMethod === 'both'}
                        onChange={() => setNotificationMethod('both')}
                      />
                      <Send size={16} />
                      <span>Both (Email + In-App)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="s-admin-ca-form-section">
            <h2 className="s-admin-ca-section-title">Review & Confirm</h2>
            <p className="s-admin-ca-section-subtitle">Review your assignment before creating</p>

            <div className="s-admin-ca-review-grid">
              <div className="s-admin-ca-review-card">
                <h3 className="s-admin-ca-card-title">Assignment Details</h3>
                <div className="s-admin-ca-review-item">
                  <span className="s-admin-ca-review-label">NAME</span>
                  <span className="s-admin-ca-review-value">{assignmentName || 'Demo'}</span>
                </div>
                <div className="s-admin-ca-review-item">
                  <span className="s-admin-ca-review-label">DESCRIPTION</span>
                  <span className="s-admin-ca-review-value">{description || 'asdgfhj'}</span>
                </div>
                <div className="s-admin-ca-review-item">
                  <span className="s-admin-ca-review-label">DUE DATE</span>
                  <span className="s-admin-ca-review-value">{dueDate || 'No deadline'}</span>
                </div>
                <div className="s-admin-ca-review-item">
                  <span className="s-admin-ca-review-label">NOTIFICATIONS</span>
                  <span className="s-admin-ca-review-value">
                    {notificationMethod === 'email' ? 'Email Only' :
                     notificationMethod === 'inapp' ? 'In-App Only' : 'Both'}
                  </span>
                </div>
                {enableReminders && (
                  <div className="s-admin-ca-review-item">
                    <span className="s-admin-ca-review-label">REMINDERS</span>
                    <span className="s-admin-ca-review-value">{reminderTiming}</span>
                  </div>
                )}
              </div>

              <div className="s-admin-ca-review-card">
                <h3 className="s-admin-ca-card-title">
                  <Users size={18} />
                  Selected Users ({selectedUsers.length})
                </h3>
                <div className="s-admin-ca-review-users">
                  {selectedUsers.map(user => (
                    <div key={user.id} className="s-admin-ca-review-user">
                      <div className="s-admin-ca-user-avatar-sm">{user.avatar}</div>
                      <div>
                        <div className="s-admin-ca-review-user-name">{user.name}</div>
                        <div className="s-admin-ca-review-user-title">{user.jobTitle}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="s-admin-ca-review-card ca-items-review">
              <h3 className="s-admin-ca-card-title">
                <BookOpen size={18} />
                Selected Learning Items ({selectedItems.length})
              </h3>
              <div className="s-admin-ca-review-items">
                {selectedItems.map(item => (
                  <div key={item.id} className="s-admin-ca-review-item-card">
                    <div className="s-admin-ca-review-item-icon">
                      <BookOpen size={20} />
                    </div>
                    <div>
                      <div className="s-admin-ca-review-item-name">{item.title}</div>
                      <div className="s-admin-ca-review-item-meta">
                        {item.type} • {item.duration} • {item.modules} modules
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="s-admin-ca-container">
      <div className="s-admin-ca-stepper">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className={`s-admin-ca-step ${currentStep === step.number ? 's-admin-ca-step-active' : currentStep > step.number ? 's-admin-ca-step-completed' : ''}`}>
              <div className="s-admin-ca-step-icon">
                <step.icon size={20} />
              </div>
              <span className="s-admin-ca-step-title">{step.title}</span>
            </div>
            {index < steps.length - 1 && <div className="s-admin-ca-step-connector" />}
          </React.Fragment>
        ))}
      </div>

      <div className="s-admin-ca-content">
        {renderStepContent()}
      </div>

      <div className="s-admin-ca-form-actions">
        {currentStep > 1 && (
          <button className="s-admin-ca-btn ca-btn-secondary" onClick={handleBack}>
            <ArrowLeft size={16} />
            Back
          </button>
        )}
        {currentStep < 5 && (
          <button className="s-admin-ca-btn ca-btn-primary" onClick={handleNext}>
            Next: {steps[currentStep]?.title} <ArrowRight size={16} />
          </button>
        )}
        {currentStep === 5 && (
          <button className="s-admin-ca-btn ca-btn-success">
            <Send size={16} />
            Create Assignment
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateAssignment;
